import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { BusyIcon, runLoading } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useAppTheme } from '../../theme/useAppTheme';
import { useModalKeys } from '../../hooks/useModalKeys';

// ─────────────────────────────────────────────────────────────────────────────
// All Param Results popup — ALPACA parity (ReportingViewController.cs).
//
//   Open  : AllParamResults_Execute (.cs:6909) — every analyte (across every
//           TestMethod) of the SampleLogIns the user ticked in the detail grid,
//           filtered to Pending Reporting + Reported.
//   Edit  : each analyte has a checkbox (checked = reportable).
//   Save  : Dc_Accepting (.cs:4578) — persists NotParamReport = !checked per
//           SampleParameter (PUT .../notparamreport).  Unchecked analytes are
//           then excluded from the rendered report (the render's not-report
//           filter drops NotParamReport=1).
//
// This is a DEDICATED editable popup (not the shared read-only <ActionPopup>) so
// the author-gated Case Narrative / Project Notes flows stay untouched.  It
// mirrors <ActionPopup>'s chrome (title bar, search, grid, footer) so the user
// sees the same look across every Custom Reporting popup.
// ─────────────────────────────────────────────────────────────────────────────

interface AllParamRow {
  parameterOid:  string;
  jobId:         string | null;
  sampleId:      string | null;
  clientSample:  string | null;
  matrix:        string | null;
  test:          string | null;
  method:        string | null;
  parameter:     string | null;
  numericResult: string | null;
  result:        string | null;
  finalResult:   string | null;
  status:        string | null;
  notParamReport: boolean;
}

// ALPACA All Param Results columns (per the 2026-06-07 user reorder):
//   JobID | SampleID | ClientSampleID | Matrix | Test | Method | Parameter |
//   NumericResult | Result | Status
const COLS: { key: keyof AllParamRow; label: string; width: number }[] = [
  { key: 'jobId',         label: 'JobID',          width: 100 },
  { key: 'sampleId',      label: 'SampleID',       width: 120 },
  { key: 'clientSample',  label: 'ClientSampleID', width: 120 },
  { key: 'matrix',        label: 'Matrix',         width:  80 },
  { key: 'test',          label: 'Test',           width: 160 },
  { key: 'method',        label: 'Method',         width: 110 },
  { key: 'parameter',     label: 'Parameter',      width: 160 },
  { key: 'numericResult', label: 'NumericResult',  width: 100 },
  { key: 'result',        label: 'Result',         width: 100 },
  { key: 'status',        label: 'Status',         width: 110 },
];

interface Props {
  title:         string;
  /** POST endpoint returning the analyte rows (body = selected ParameterOids). */
  fetchEndpoint: string;
  /** The selected ParameterOids sent as the POST body. */
  fetchBody:     string[];
  /** PUT endpoint persisting [{ parameterOid, notParamReport }]. */
  saveEndpoint:  string;
  onClose:       () => void;
  /** Called after a successful save (e.g. to refresh the detail grid). */
  onSaved?:      () => void;
}

export function AllParamsPopup({ title, fetchEndpoint, fetchBody, saveEndpoint, onClose, onSaved }: Props) {
  const theme  = useAppTheme();
  const notify = useNotify();
  const [rows,    setRows]    = useState<AllParamRow[]>([]);
  const [load,    setLoad]    = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [busy,    setBusy]    = useState(false);
  const [search,  setSearch]  = useState('');
  // checked = the analyte is SELECTED TO REPORT (NotParamReport will be set false).
  // ALPACA parity: the popup opens with EVERY analyte UNCHECKED and the user CHECKS
  // the ones to include; on Save each analyte gets NotParamReport = !checked
  // (checked = reported, unchecked = excluded).  Keyed by parameterOid.
  const [checked, setChecked] = useState<Set<string>>(new Set());

  // ── Load the analyte rows for the ticked samples ────────────────────────
  useEffect(() => {
    let alive = true;
    setLoad(true);
    setErr(null);
    api.post(fetchEndpoint, fetchBody ?? [])
      .then(res => {
        if (!alive) return;
        const data = (res.data as { data?: AllParamRow[] }).data ?? [];
        setRows(data);
        // ALPACA parity (AllParamResults): open with ALL analytes UNCHECKED — the
        // legacy grid has no pre-selection (GridView_PreRender skips this view, so
        // it starts empty; Dc_Accepting L4578 then sets NotParamReport = !isChecked
        // for every analyte on OK).  The user checks the analytes to include.
        setChecked(new Set());
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setErr((e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message ?? 'Failed to load.');
      })
      .finally(() => { if (alive) setLoad(false); });
    return () => { alive = false; };
    // fetchBody is stringified for a stable dep-check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEndpoint, JSON.stringify(fetchBody)]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      COLS.some(c => String(r[c.key] ?? '').toLowerCase().includes(t)));
  }, [rows, search]);

  const toggle = (oid: string) => {
    setChecked(prev => {
      const n = new Set(prev);
      if (n.has(oid)) n.delete(oid); else n.add(oid);
      return n;
    });
  };

  // Dirty when saving would change at least one analyte's NotParamReport (= !checked)
  // vs. what's persisted — so Save is a no-op only when the checks already match the
  // stored state (e.g. every reportable analyte re-checked).
  const dirty = useMemo(
    () => rows.some(r => (!checked.has(r.parameterOid)) !== r.notParamReport),
    [rows, checked]);

  const onSave = async () => {
    // Persist NotParamReport = !checked for every analyte whose resulting state
    // differs from what's stored (checked → reported, unchecked → excluded).
    const updates = rows
      .filter(r => (!checked.has(r.parameterOid)) !== r.notParamReport)
      .map(r => ({ parameterOid: r.parameterOid, notParamReport: !checked.has(r.parameterOid) }));
    if (updates.length === 0) { onClose(); return; }
    setBusy(true);
    try {
      await runLoading(api.put(saveEndpoint, updates), { blocking: true, label: 'Saving…' });
      notify.success('Saved successfully.');   // post-operation acknowledgement (§18.6)
      onSaved?.();
      onClose();
    } catch (e: unknown) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const allChecked = filtered.length > 0 && filtered.every(r => checked.has(r.parameterOid));

  // Bespoke popup keyboard: Esc closes (standing rule 14). Rendered only while
  // open, so the hook is always active here.
  const modalKeys = useModalKeys(true, { onClose });

  return (
    <div onClick={onClose}
         style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,.35)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} {...modalKeys}
           style={{ width: 1100, maxWidth: '95vw', maxHeight: '90vh', background: '#fff',
                    borderRadius: 4, boxShadow: '0 6px 24px rgba(0,0,0,.25)',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Title bar — same chrome as the other Custom Reporting popups */}
        <div style={{ padding: '8px 14px', background: theme.accent, color: '#fff',
                      fontSize: 13, fontWeight: 600, display: 'flex',
                      alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{title}</span>
          <button onClick={onClose}
                  style={{ background: 'transparent', border: 'none', color: '#fff',
                           fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
                  title="Close">×</button>
        </div>

        {/* Sub-toolbar: search + count + Save */}
        <div style={{ padding: '6px 12px', background: 'var(--ai-header-bg)',
                      borderBottom: '1px solid var(--ai-line)',
                      display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="list-search" style={{ width: 240 }}>
            <span className="tb-i">🔍</span>
            <input type="text" value={search}
                   onChange={e => setSearch(e.target.value)} placeholder="Search…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span style={{ fontSize: 11, color: 'var(--ai-ink)' }}>
            {checked.size} of {rows.length} checked to report
          </span>
          <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
            · check the analytes to include; unchecked are excluded on Save
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="ca-btn ca-btn-solid" onClick={onSave}
                    disabled={busy || !dirty}
                    style={{ padding: '3px 10px', fontSize: 11 }}
                    title="Save — checked analytes report; unchecked analytes are excluded from the report">
              <BusyIcon busy={busy} icon="ti-device-floppy" /> Save
            </button>
            <button className="ca-btn" onClick={onClose}
                    style={{ padding: '3px 10px', fontSize: 11 }}>Cancel</button>
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflow: 'auto', borderTop: '1px solid var(--ai-line)' }}>
          {load && <div style={{ padding: 14, textAlign: 'center', color: 'var(--ai-muted)' }}>Loading…</div>}
          {err && !load && <div style={{ padding: 12, color: 'var(--ai-red)', fontSize: 12 }}>{err}</div>}
          {!load && !err && filtered.length === 0 && (
            <div style={{ padding: 18, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 12 }}>
              No analytes for the selected sample(s).
            </div>
          )}
          {!load && !err && filtered.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', fontSize: 12 }}>
              <thead>
                <tr style={{ background: theme.featureBg }}>
                  <th style={{ ...thStyle, width: 36, textAlign: 'center' }}>
                    <input type="checkbox"
                           checked={allChecked}
                           ref={el => { if (el) el.indeterminate =
                             filtered.some(r => checked.has(r.parameterOid)) && !allChecked; }}
                           onChange={() => {
                             setChecked(prev => {
                               const n = new Set(prev);
                               if (allChecked) filtered.forEach(r => n.delete(r.parameterOid));
                               else            filtered.forEach(r => n.add(r.parameterOid));
                               return n;
                             });
                           }}
                           title={allChecked ? 'Uncheck all visible' : 'Check all visible'} />
                  </th>
                  {COLS.map(c => (
                    <th key={c.key} style={{ ...thStyle, width: c.width }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.parameterOid}>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox"
                             checked={checked.has(r.parameterOid)}
                             onChange={() => toggle(r.parameterOid)} />
                    </td>
                    {COLS.map(c => (
                      <td key={c.key} style={tdStyle} title={String(r[c.key] ?? '')}>
                        {String(r[c.key] ?? '') || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '6px 8px', textAlign: 'left', fontWeight: 600,
  borderBottom: '1px solid var(--ai-line)', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, background: 'inherit',
};
const tdStyle: React.CSSProperties = {
  padding: '4px 8px', borderBottom: '1px solid var(--ai-line)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240,
};
